import { isValidElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../atoms/Badge";
import { cn } from "../../lib/utils";
import { SINGLE_LINE_BADGE_LABELS } from "./badgeListStyles";
import { useBadgeListFit } from "./useBadgeListFit";

export interface TableBadgeListFittedRowProps {
  /** Keyed badge elements, as `Children.toArray` returns them. */
  badges: ReactNode[];
  leading?: ReactNode;
  /** Wrap across the lines a capped `expand` cell allows, rather than one line. */
  multiline?: boolean;
  className?: string;
}

export const TableBadgeListFittedRow = ({ badges, leading, multiline, className }: TableBadgeListFittedRowProps) => {
  const { t } = useTranslation();
  const badgesKey = badges.map((badge) => (isValidElement(badge) ? String(badge.key) : "")).join("\n");
  const { boundsRef, measureRef, fitCount, hiddenLabels } = useBadgeListFit(badges.length, { multiline, badgesKey });
  const hiddenCount = badges.length - fitCount;

  return (
    <div ref={boundsRef} className={cn("relative w-full min-w-0 overflow-hidden", SINGLE_LINE_BADGE_LABELS, className)}>
      <div
        data-slot="table-badge-list"
        className={cn("flex items-center gap-1", multiline ? "flex-wrap" : "flex-nowrap")}
      >
        {leading !== undefined && <div className="flex shrink-0 items-center">{leading}</div>}
        {badges.slice(0, fitCount)}
        {hiddenCount > 0 && (
          <Badge variant="outline" hoverLabel={hiddenLabels.join(", ")}>
            {fitCount === 0
              ? t("{{amount}} items", { amount: badges.length })
              : t("+ {{amount}} more", { amount: hiddenCount })}
          </Badge>
        )}
      </div>
      <div
        ref={measureRef}
        data-slot="table-badge-list-measure"
        aria-hidden
        inert
        // In flow at zero height rather than absolute: outside a table, its max-content width is what lets
        // the list grow back when its container widens, since a content-sized parent would otherwise hold
        // it at the width of the badges it already shows.
        className="pointer-events-none invisible relative flex h-0 w-max flex-nowrap items-center gap-1 overflow-hidden"
      >
        {leading !== undefined && (
          <div data-badge-list-leading="" className="flex shrink-0 items-center">
            {leading}
          </div>
        )}
        {badges}
        {/* The widest the "+ N more" badge can get, so reserving its width never under-counts. */}
        <Badge variant="outline">{t("+ {{amount}} more", { amount: badges.length })}</Badge>
      </div>
    </div>
  );
};
